import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const VIDEOS = [
  {
    src: "/assets/videos/testimonial-1.mp4",
    poster: "/assets/videos/testimonial-1.jpg",
    title: "A Message from Team PK",
    meta: "Promotional · 0:52",
  },
  {
    src: "/assets/videos/testimonial-2.mp4",
    poster: "/assets/videos/testimonial-2.jpg",
    title: "Why Homeowners Choose Us",
    meta: "Client Story · 1:23",
  },
];

export const VideoCard = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: index * 0.15, ease: EASE }}
      className="group relative"
      data-testid={`video-card-${index + 1}`}
    >
      <div className="gold-frame relative overflow-hidden rounded-[1.6rem] border border-[#6CBE45]/20 bg-[#0F1A1C] shadow-[0_30px_70px_-22px_rgba(74,143,42,0.4)] aspect-[478/850]">
        {playing ? (
          <video
            src={video.src}
            poster={video.poster}
            controls
            autoPlay
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            data-testid={`video-player-${index + 1}`}
          />
        ) : (
          <>
            <img
              src={video.poster}
              alt={video.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A1C]/70 via-transparent to-transparent" />
            <button
              onClick={() => setPlaying(true)}
              data-testid={`video-play-${index + 1}`}
              aria-label={`Play video: ${video.title}`}
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
            >
              <span className="wa-pulse w-18 h-18 sm:w-20 sm:h-20 rounded-full bg-[#6CBE45] text-white flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-[0_12px_35px_-8px_rgba(108,190,69,0.7)]">
                <Play className="w-8 h-8 ml-1 fill-white" />
              </span>
            </button>
          </>
        )}
      </div>
      <div className="mt-5 text-center">
        <p className="font-semibold text-lg tracking-tight">{video.title}</p>
        <p className="mt-1 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#4A8F2A]">
          {video.meta}
        </p>
      </div>
    </motion.div>
  );
};

export default function VideoTestimonials() {
  return (
    <section
      id="client-videos"
      className="relative py-24 lg:py-32 overflow-hidden grain"
      data-testid="client-videos-section"
    >
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(108,190,69,0.12),transparent_65%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono-accent text-xs font-semibold uppercase tracking-[0.28em] text-[#4A8F2A] mb-5 text-center"
        >
          Client Testimonials
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-center max-w-3xl mx-auto"
        >
          Hear it{" "}
          <span className="font-serif-accent italic font-semibold text-[#4A8F2A]">
            straight
          </span>{" "}
          from us
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-5 text-center text-[#4A5A5E] max-w-xl mx-auto text-sm sm:text-base leading-relaxed"
        >
          Real words, real work — a quick look at who we are and why families
          trust Team PK with their homes.
        </motion.p>

        <div className="mt-14 grid sm:grid-cols-2 gap-8 lg:gap-14 max-w-3xl mx-auto">
          {VIDEOS.map((v, i) => (
            <VideoCard key={v.src} video={v} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
